import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-hot-toast';
// import { motion, AnimatePresence } from 'framer-motion';

const FeedbackSurveyModal = ({ isOpen, onClose, editingSurvey = null, clients = [] }) => {
  const [surveyData, setSurveyData] = useState({
    title: '',
    description: '',
    questions: [],
    targetClients: [],
    isActive: true,
    allowAnonymous: false,
    sendReminder: true,
    reminderDays: 3
  });
  
  const [isSaving, setIsSaving] = useState(false); // eslint-disable-line no-unused-vars
  const [showTemplates, setShowTemplates] = useState(false); // eslint-disable-line no-unused-vars
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'text',
    required: true,
    options: []
  });

  const auth = getAuth();
  const firestore = getFirestore();

  // Predefined question templates
  const questionTemplates = [
    {
      category: 'Photography Experience',
      questions: [
        { text: 'What did you love most about the photography process?', type: 'text', required: true },
        { text: 'How would you rate your overall experience?', type: 'rating', required: true },
        { text: 'Were the photos delivered within the expected timeframe?', type: 'yes_no', required: true },
        { text: 'How professional was the photographer during the session?', type: 'rating', required: true },
        { text: 'Would you recommend our photography services to others?', type: 'yes_no', required: true }
      ]
    },
    {
      category: 'Photo Quality & Usability',
      questions: [
        { text: 'Are the photos usable for your intended purposes?', type: 'yes_no', required: true },
        { text: 'How would you rate the quality of the final photos?', type: 'rating', required: true },
        { text: 'Do the photos meet your expectations?', type: 'yes_no', required: true },
        { text: 'Were there enough photos delivered in the final gallery?', type: 'yes_no', required: false },
        { text: 'What would you change about the photo editing style?', type: 'text', required: false }
      ]
    },
    {
      category: 'Service & Communication',
      questions: [
        { text: 'How was the communication throughout the process?', type: 'rating', required: true },
        { text: 'Were your specific requests and preferences addressed?', type: 'yes_no', required: true },
        { text: 'How easy was it to schedule and coordinate the session?', type: 'rating', required: false },
        { text: 'Did you feel comfortable during the photography session?', type: 'yes_no', required: true }
      ]
    },
    {
      category: 'Improvement & Future',
      questions: [
        { text: 'Do you envision an alternative approach for future sessions?', type: 'text', required: false },
        { text: 'What could we improve for next time?', type: 'text', required: false },
        { text: 'Would you book another session with us?', type: 'yes_no', required: false },
        { text: 'What additional services would you be interested in?', type: 'multiple_choice', required: false, options: ['Event Photography', 'Portrait Sessions', 'Commercial Photography', 'Photo Editing Services', 'Print Services'] }
      ]
    }
  ];

  useEffect(() => {
    if (editingSurvey) {
      setSurveyData(editingSurvey);
    } else {
      setSurveyData({
        title: '',
        description: '',
        questions: [],
        targetClients: [],
        isActive: true,
        allowAnonymous: false,
        sendReminder: true,
        reminderDays: 3
      });
    }
  }, [editingSurvey, isOpen]);

  const handleSaveSurvey = async () => {
    if (!surveyData.title.trim()) {
      toast.error('Please enter a survey title');
      return;
    }

    if (surveyData.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    setIsSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Authentication required');
      }

      const surveyToSave = {
        ...surveyData,
        userId: currentUser.uid,
        created: editingSurvey ? surveyData.created : new Date().toISOString(),
        updated: new Date().toISOString(),
        responseCount: editingSurvey ? surveyData.responseCount || 0 : 0
      };

      if (editingSurvey) {
        await updateDoc(doc(firestore, 'surveys', editingSurvey.id), surveyToSave);
        toast.success('Survey updated successfully');
      } else {
        await addDoc(collection(firestore, 'surveys'), surveyToSave);
        toast.success('Survey created successfully');
      }

      onClose();
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey');
    } finally {
      setIsSaving(false);
    }
  };

  const addQuestion = () => {
    if (!newQuestion.text.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setSurveyData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...newQuestion, id: Date.now().toString() }]
    }));

    setNewQuestion({
      text: '',
      type: 'text',
      required: true,
      options: []
    });
  };

  const addTemplateQuestion = (question) => {
    setSurveyData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...question, id: Date.now().toString() }]
    }));
  };

  const removeQuestion = (questionId) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const updateQuestion = (questionId, updates) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, ...updates } : q
      )
    }));
  };

  const handleAddOption = (questionId) => {
    updateQuestion(questionId, {
      options: [...(surveyData.questions.find(q => q.id === questionId)?.options || []), '']
    });
  };

  const handleUpdateOption = (questionId, optionIndex, value) => {
    const question = surveyData.questions.find(q => q.id === questionId);
    const newOptions = [...question.options];
    newOptions[optionIndex] = value;
    updateQuestion(questionId, { options: newOptions });
  };

  const handleRemoveOption = (questionId, optionIndex) => {
    const question = surveyData.questions.find(q => q.id === questionId);
    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    updateQuestion(questionId, { options: newOptions });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#111111] border-2 border-gray-200 dark:border-[#1A1A1A] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-6 border-b-2 border-gray-200 dark:border-[#1A1A1A]">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white tracking-tight">
            Create Feedback Survey
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white transition-colors p-2 hover:bg-gray-100 dark:hover:bg-[#252525] rounded-lg"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-400">Survey creation functionality coming soon...</p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackSurveyModal; 